import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'

import { NoConnectionPage } from './NoConnection.page'

import type { ReactNode } from 'react'

type Props = {
  children: ReactNode | ReactNode[]
}

const NetworkConnected = ({ children }: Props) => {
  const [isOnline, setIsOnline] = useState<boolean>(true)
  const overlayRef = useRef<HTMLDivElement>(null)

  const handleGoOffline = useCallback(() => {
    setIsOnline(false)
  }, [])

  const handleGoOnline = useCallback(() => {
    setIsOnline(true)
  }, [])

  // The `online` event can be missed (e.g. while a native WebView is
  // suspended), so retrying re-checks the actual connection state.
  const handleRetry = useCallback(() => {
    setIsOnline(navigator.onLine)
  }, [])

  useEffect(() => {
    window.addEventListener('offline', handleGoOffline)
    window.addEventListener('online', handleGoOnline)

    return () => {
      window.removeEventListener('offline', handleGoOffline)
      window.removeEventListener('online', handleGoOnline)
    }
  }, [handleGoOffline, handleGoOnline])

  // The app stays mounted underneath the offline screen so in-progress state,
  // such as a booking flow, survives a dropped connection. Every other body
  // child — the React root and portalled modals, sheets and menus — is made
  // inert so keyboard and screen reader users stay on the offline screen.
  useEffect(() => {
    if (isOnline) return

    const covered = [...document.body.children].filter((element) => element !== overlayRef.current)
    for (const element of covered) element.setAttribute('inert', '')

    return () => {
      for (const element of covered) element.removeAttribute('inert')
    }
  }, [isOnline])

  return (
    <>
      {children}
      {isOnline
        ? null
        : createPortal(
            <StOfflineOverlay ref={overlayRef}>
              <NoConnectionPage onRetry={handleRetry} />
            </StOfflineOverlay>,
            document.body,
          )}
    </>
  )
}

// Portalled to body because #react-root is its own stacking context; this
// sits above body-level portals, including select menus (9999).
const StOfflineOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background-color: ${({ theme }) => theme.Colors.Background.White};
`

export { NetworkConnected }
